import React, { useCallback, useEffect, useState, useTransition } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, List, ListItem, ListItemText, IconButton, Box, CircularProgress, Stack, Divider } from '@mui/material';
import { Delete, Download } from '@mui/icons-material';
import { serviceModulesApi } from '../../../api/serviceModulesApi';
import { PersonalDocumentResponse, PersonalDocumentEmployeeResponse } from '../../../types/personalDocuments';
import { downloadFileFromUrl } from '../../../common/logic/FileDownloader';
import ConfirmDeleteDialog from '../../../components/common/table/ConfirmDeleteDialog';
import { useTranslation } from 'react-i18next';
import { GeneralButton } from '../../../common/ui/GeneralButton';
import dayjs from 'dayjs';
import { useAppDispatch } from '../../../hooks/StoreHooks';
import { handleRequest } from '../../../store/utils/handleRequest';

interface Props {
  open: boolean;
  onClose: () => void;
  serviceModuleId: number;
  employee: PersonalDocumentEmployeeResponse | null;
}

export const PersonalDocumentsDialogEmployee: React.FC<Props> = ({ open, onClose, serviceModuleId, employee }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [documents, setDocuments] = useState<PersonalDocumentResponse[] | undefined>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const [deleteDoc, setDeleteDoc] = useState<PersonalDocumentResponse | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  useEffect(() => {
    if (open && employee) {
      loadDocuments();
    } else {
      setDocuments([]);
      }
  }, [open, employee, serviceModuleId]);
  
  const loadDocuments = useCallback(async (showLoader = true) => {
    if (employee) {
      if (showLoader) setIsLoading(true);
      const response = await serviceModulesApi.getPersonalDocumentsForEmployee(serviceModuleId, employee.employeeId);
      if (response.success() && response.result) {
        setDocuments(response.result);
      }
      else {
        setDocuments(undefined);
      }
      if (showLoader) setIsLoading(false);
    }
  }, [employee, serviceModuleId]);

  const handleDownload = (doc: PersonalDocumentResponse) => {
    if (doc.blobPath && doc.filename) {
      downloadFileFromUrl(doc.blobPath, doc.filename);
    }
  };

  const handleDeleteClick = (doc: PersonalDocumentResponse) => {
    setDeleteDoc(doc);
    setIsConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (employee && deleteDoc) {
      const response = await handleRequest(
        dispatch,
        "direct",
        () => serviceModulesApi.deletePersonalDocumentFromEmployee(serviceModuleId, employee.employeeId, deleteDoc.id),
        t("requestMessage.personalDocumentDeleted")
      );
      if (response.success()) {
        loadDocuments(false);
      }
    };
    setDeleteDoc(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t("serviceModulesPage.personalDocumentsPage.personalDocumentsDialogEmployee.title")} {employee?.firstName} {employee?.lastName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ 
            position: 'sticky', 
            top: 0, 
            zIndex: 1,
            py: 1,
            display: 'flex', 
            alignItems: 'center', 
            gap: 1 ,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper', 
          }}>
            <Typography variant="caption">
              {t("serviceModulesPage.personalDocumentsPage.shared.documentsTotalcount")}: {documents?.length}
            </Typography>
          </Box>
          <PersonalDocumentsListDocuments
            isLoading={isLoading}
            documents={documents}
            onDownloadClick={handleDownload}
            onDeleteClick={handleDeleteClick}
          />
        </DialogContent>
        <DialogActions>
          <GeneralButton onClick={onClose} appVariant="secondary">
            {t("buttons.close")}
          </GeneralButton>
        </DialogActions>
      </Dialog>

      <ConfirmDeleteDialog
        open={isConfirmDialogOpen}
        dialogMessage={t("serviceModulesPage.personalDocumentsPage.shared.confirmDelete")}
        handleDialogClose={() => setIsConfirmDialogOpen(false)}
        handleOnDelete={handleConfirmDelete}
      />
    </>
  );
};

interface PersonalDocumentsListDocumentsProps {
  isLoading: boolean;
  documents: PersonalDocumentResponse[] | undefined;
  onDownloadClick: (document: PersonalDocumentResponse) => void;
  onDeleteClick: (document: PersonalDocumentResponse) => void;
}

const PersonalDocumentsListDocuments: React.FC<PersonalDocumentsListDocumentsProps> = ({ isLoading, documents, onDownloadClick, onDeleteClick }) => {
  const { t } = useTranslation();

  if (isLoading && (!documents || documents.length === 0)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (documents === undefined) {
    return (
      <Typography color="textSecondary" sx={{ py: 2 }}>
        {t("serviceModulesPage.personalDocumentsPage.shared.failedLoading")}
      </Typography>
    );
  }

  if (documents.length === 0) {
    return (
      <Typography color="textSecondary" sx={{ py: 2 }}>
        {t("serviceModulesPage.personalDocumentsPage.shared.noDocumentsAvailable")}
      </Typography>
    );
  }

  return (
    <List>
      {documents.map((doc, index) => (
        <React.Fragment key={doc.id}>
          <ListItem
            secondaryAction={
              <Stack direction="row" spacing={1}>
                <IconButton edge="end" onClick={() => onDownloadClick(doc)} title={t("buttons.download")}>
                  <Download />
                </IconButton>
                <IconButton edge="end" onClick={() => onDeleteClick(doc)} title={t("buttons.delete")} color="error">
                  <Delete />
                </IconButton>
              </Stack>
            }
          >
            <ListItemText
              primary={doc.filename}
              secondary={
                <Typography variant="caption" component="span" sx={{ display: "block" }} color="textSecondary">
                  {t("serviceModulesPage.personalDocumentsPage.shared.uploadedAt")}: {dayjs(doc.uploadDatetime).format("YYYY-MM-DD HH:mm")}
                </Typography>
              }
            />
          </ListItem>
          {index < documents.length - 1 && <Divider component="li" />}
        </React.Fragment>
      ))}
    </List>
  );
}
